import EventBus from 'components/event-bus';
import auth from 'controllers/auth';
import connector from 'controllers/connector';
import {
	AjaxRequestErrorEvent,
	AjaxSessionExpiredEvent,
} from 'interfaces/app';
import * as DialogService from 'services/dialog';
import {
	ChannelsModule,
	UserModule,
} from 'store';

/*
 * Dialog-based policy for ajax transport failures.
 *
 * The ajax controller is a transport primitive imported by everything that
 * talks to the server, so it must not know about dialogs, the store, or the
 * social connector. It emits `ajax:session-expired` / `ajax:request-error`
 * events with retry/fail controls, and this module — registered once at app
 * boot — owns the user-facing handling.
 */

/**
 * Handles `ajax:session-expired`: logs the user out and shows the
 * expired-session dialog. Re-running the original request is deferred until the
 * user re-authenticates — on the next `auth:login`, calls `retry()` when login
 * succeeded or `fail()` when it was declined, settling the pending request.
 *
 * @param event - Session-expired payload from the ajax transport.
 * @param event.fail - Rejects the original request with an Unauthorized error.
 * @param event.retry - Re-runs the original request with a fresh bearer token.
 */
function onSessionExpired({
	fail,
	retry,
}: AjaxSessionExpiredEvent): void {
	/* User is no longer logged in, so reset all user details and permissions. */
	UserModule
		.logout()
		.finally(() => {
			const closeError = DialogService.openErrorDialog({
				header: {
					hasCloseButton: false,
					title: window.App.router.$t('dialogHeaderExpired'),
				},
				body: {
					content: window.App.router.$t('dialogTextExpired'),
				},
				footer: {
					buttons: [
						{
							id: 'accept',
							text: window.App.router.$t('dialogButtonExpiredOk'),
							click: () => {
								EventBus.once(
									'auth:login',
									(success: boolean) => {
										if (success) {
											retry();
										} else {
											fail();
										}
									},
								);

								auth.showLogin({
									hasclose: false,
									showGuest: false,
									showGoogleLogin: !!(connector.isSupported('gplus') && ChannelsModule.getById('gplus')?.login),
								});
								closeError();
							},
						},
					],
				},
			});
		});
}

/**
 * Handles `ajax:request-error`: shows the generic error dialog for a non-401
 * failure. The dialog always offers retry; the second button is either abort
 * (reject with the server message) when `allowAbort` is set, or a reload-the-app
 * reset otherwise.
 *
 * @param event - Request-error payload from the ajax transport.
 * @param event.allowAbort - Offer an abort button instead of a reload/reset.
 * @param event.errorMessage - Server-provided message shown in the dialog body.
 * @param event.fail - Rejects the original request with the error message.
 * @param event.retry - Re-runs the original request with a fresh bearer token.
 */
function onRequestError({
	allowAbort,
	errorMessage,
	fail,
	retry,
}: AjaxRequestErrorEvent): void {
	let dialogErrorText = window.App.router.$t('dialogTextError');
	dialogErrorText += `\n\nError: ${errorMessage}`;
	let closeError: () => void = () => {};

	const errorButtonCollection: DialogServiceButton[] = [
		{
			id: 'accept',
			text: window.App.router.$t('dialogButtonErrorRetry'),
			click: () => {
				retry();
				closeError();
			},
		},
	];

	if (allowAbort) {
		errorButtonCollection.unshift({
			id: 'abort',
			text: window.App.router.$t('dialogButtonErrorAbort'),
			click: () => {
				fail();
				closeError();
			},
		});
	} else {
		errorButtonCollection.unshift({
			id: 'reset',
			text: window.App.router.$t('dialogButtonErrorReset'),
			click: () => {
				DialogService.openLoaderDialog();
				window.location.reload();
				closeError();
			},
		});
	}

	closeError = DialogService.openErrorDialog({
		header: {
			hasCloseButton: false,
			title: window.App.router.$t('dialogHeaderError'),
		},
		body: {
			content: dialogErrorText,
		},
		footer: {
			buttons: errorButtonCollection,
		},
	});
}

/**
 * Subscribes the dialog-based failure handlers to the ajax transport events.
 * Must run once during app boot, before the first authenticated request.
 */
export function installAjaxDialogHandlers(): void {
	EventBus.on(
		'ajax:session-expired',
		onSessionExpired,
	);
	EventBus.on(
		'ajax:request-error',
		onRequestError,
	);
}
